import RuleBuilder from 'services/simplex/rule-builder';
import RandomTeamsStackMarker from 'components/organisms/random-team-stacking/services/random-teams-stack-marker';
import {addRoleSuffix, getPlayersGroupUID, getSimplexUnitName} from 'helpers/simplex-helper';
import Const from 'constants';
import {countEntries, getRandomArray} from 'helpers/array-helper';
import {calcAndRoundDownValByPct} from 'helpers/common-helper';

function removePlayersByDisallowedPositions(players, disallowedPositions) {
    return players.filter((player) => !inArray(player.positionName, disallowedPositions));
}

function createConstraintsForPlayersInTeam(params) {
    const {stacking, mappedPlayersByTeams, modes} = params;
    const checkedTeams = stacking.getActiveCheckedTeams();
    const groups = stacking.getGroups();

    const constraints = {};
    Object.keys(groups).map((groupNumber) => {
        const playersQuantity = stacking.getPlayersQuantityByGroupNumber(groupNumber);

        Object.keys(checkedTeams).map((teamName) => {
            let players = mappedPlayersByTeams[teamName];

            if (stacking.isDisallowedPositionsUsed()) {
                const disallowedPositions = stacking.getDisallowedPositionsByGroupNumber(groupNumber);
                players = removePlayersByDisallowedPositions(players, disallowedPositions);
            }

            const builder = RuleBuilder.fromAtLeast(0);

            Object.values(players).map((player) => {
                modes.map((playerMode) => {
                    const lpPlayerId = addRoleSuffix({
                        prefix: player.getId(),
                        role: playerMode,
                    });
                    builder.addVar(getSimplexUnitName(lpPlayerId), 1);
                });
            });

            let groupName = getPlayersGroupUID(teamName, groupNumber);
            groupName = RandomTeamsStackMarker.setSuffix(groupName);
            builder.addVar(groupName, -1 * playersQuantity);

            if (!constraints.hasOwnProperty(teamName)) {
                constraints[teamName] = [];
            }
            constraints[teamName].push(builder.createRule());
        });
    });

    return {...constraints};
}

function createConstraintsForForcedCaptains(params) {
    const {stacking, mappedPlayersByTeams, modes, teams, groupNumber} = params;
    const constraints = {};

    teams.map((teamName) => {
        let players = mappedPlayersByTeams[teamName];

        if (stacking.isDisallowedPositionsUsed()) {
            const disallowedPositions = stacking.getDisallowedPositionsByGroupNumber(groupNumber);
            players = removePlayersByDisallowedPositions(players, disallowedPositions);
        }

        const builder = RuleBuilder.fromAtLeast(0);

        Object.values(players).map((player) => {
            modes.map((playerMode) => {
                const lpPlayerId = addRoleSuffix({
                    prefix: player.getId(),
                    role: playerMode,
                });
                builder.addVar(getSimplexUnitName(lpPlayerId), 1);
            });
        });

        let groupName = getPlayersGroupUID(teamName, groupNumber);
        groupName = RandomTeamsStackMarker.setSuffix(groupName);
        builder.addVar(groupName, -1);

        constraints[teamName] = builder.createRule();
    });

    return {...constraints};
}

function createConstraintsForSameTeamInStackingGroups(params) {
    const {stacking, teams} = params;
    const groups = stacking.getGroups();

    const constraints = {};
    teams.map((teamName) => {
        const builder = RuleBuilder.fromAtMost(1);

        Object.keys(groups).map((groupNumber) => {
            let groupName = getPlayersGroupUID(teamName, groupNumber);
            groupName = RandomTeamsStackMarker.setSuffix(groupName);
            builder.addVar(groupName, 1);
        });
        constraints[teamName] = builder.createRule();
    });

    return constraints;
}

function createConstraintsForTeamsInStackingGroup(params) {
    const {stacking, teams} = params;
    const groups = stacking.getGroups();

    const constraints = {};
    Object.keys(groups).map((groupNumber) => {
        const builder = RuleBuilder.fromExactly(1);

        teams.map((teamName) => {
            let groupName = getPlayersGroupUID(teamName, groupNumber);
            groupName = RandomTeamsStackMarker.setSuffix(groupName);
            builder.addVar(groupName, 1);
        });
        if (builder.isRuleCreatable()) {
            constraints[groupNumber] = builder.createRule();
        }
    });

    return constraints;
}

function createConstraintsForDesirableTeams(params) {
    const {stacking, usedTeams, teams, lineupsQuantity} = params;
    const groups = stacking.getGroups();

    const result = [];

    const builder = RuleBuilder.fromAtLeast(1);

    teams.map((teamName) => {
        const expMin = stacking.getTeamMinExposure(teamName);
        const reservedQuantity = calcAndRoundDownValByPct(lineupsQuantity, expMin);
        const usageQuantity = countEntries(teamName, usedTeams);

        if (0 < reservedQuantity - usageQuantity) {
            Object.keys(groups).map((groupNumber) => {
                let groupName = getPlayersGroupUID(teamName, groupNumber);
                groupName = RandomTeamsStackMarker.setSuffix(groupName);
                builder.addVar(groupName, 1);
            });
        }
    });

    if (builder.isRuleCreatable()) {
        result.push(builder.createRule());
    }

    return [...result];
}

function createObjectiveForTeams(params) {
    const {stacking} = params;
    const checkedTeams = stacking.getActiveCheckedTeams();
    const groups = stacking.getGroups();

    const objective = {};
    Object.keys(groups).map((groupNumber) => {
        Object.keys(checkedTeams).map((teamName) => {
            let groupName = getPlayersGroupUID(teamName, groupNumber);
            groupName = RandomTeamsStackMarker.setSuffix(groupName);
            objective[groupName] = 0;
        });
    });

    return {...objective};
}

function createObjectiveForRandomlyChosenPlayers(params) {
    const {projectionPointsPropNamesByModes, mappedPlayersByTeams, teams, stacking} = params;
    const randomTeams = getRandomArray(teams, 2);

    let avoidedPositions = [];
    if (stacking.isDisallowedPositionsUsed()) {
        avoidedPositions = stacking.getIntersectedDisallowedPositions();
    }

    const objective = {};
    randomTeams.map((teamName) => {
        const availablePlayers = removePlayersByDisallowedPositions(mappedPlayersByTeams[teamName], avoidedPositions);
        const randomPlayers = getRandomArray(availablePlayers, 3);

        randomPlayers.map((player) => {
            Object.keys(projectionPointsPropNamesByModes).map((mode) => {
                const lpPlayerId = addRoleSuffix({
                    prefix: player.getId(),
                    role: mode,
                });
                const propName = projectionPointsPropNamesByModes[mode];
                const projectionPoints = player[propName] * Const.RANDOM_TEAM_STACKING_PROJECTION_MULTIPLY_VALUE;

                objective[getSimplexUnitName(lpPlayerId)] = parseInt(projectionPoints);
            });
        });
    });

    return {...objective};
}

function createConstraintsForTeamsCombinations(params) {
    const {lineupTeamsCombinations, rawLineups, teams, lineupsQuantity} = params;
    const items = lineupTeamsCombinations.exposureMax.all();

    const result = [];
    Object.values(items).map((item) => {
        let usageQuantity = 0;
        rawLineups.asArr().map((lineup) => {
            const diff = arrayDiff(item.teamsCombination, lineup.getTeamNames());
            if (isEmpty(diff)) {
                usageQuantity++;
            }
        });

        const expMax = item.value;
        const usagePercent = ((usageQuantity + 1) / lineupsQuantity) * 100;

        if (usagePercent <= expMax) {
            return;
        }

        const [firstTeam, secondTeam] = item.teamsCombination;

        if (!inArray(firstTeam, teams) || !inArray(secondTeam, teams)) {
            return;
        }

        result.push(getRuleForTeamsCombination([firstTeam, secondTeam]));
        result.push(getRuleForTeamsCombination([secondTeam, firstTeam]));
    });

    function getRuleForTeamsCombination(teams) {
        const [firstTeam, secondTeam] = teams;

        const builder = RuleBuilder.fromAtMost(1);

        let firstGroupName = getPlayersGroupUID(firstTeam, 1);
        firstGroupName = RandomTeamsStackMarker.setSuffix(firstGroupName);
        builder.addVar(firstGroupName, 1);

        let secondGroupName = getPlayersGroupUID(secondTeam, 2);
        secondGroupName = RandomTeamsStackMarker.setSuffix(secondGroupName);
        builder.addVar(secondGroupName, 1);

        return builder.createRule();
    }

    return [...result];
}

function createConstraintsForExcludedTeamsFromStacks(params) {
    const {excludedTeamsFromStacks} = params;

    const result = [];

    const builder = RuleBuilder.fromExactly(0);

    Object.keys(excludedTeamsFromStacks).map((groupNumber) => {
        const teams = excludedTeamsFromStacks[groupNumber];

        teams.map((team) => {
            let groupName = getPlayersGroupUID(team, groupNumber);
            groupName = RandomTeamsStackMarker.setSuffix(groupName);
            builder.addVar(groupName, 1);
        });
    });

    if (builder.isRuleCreatable()) {
        result.push(builder.createRule());
    }

    return [...result];
}

class ConstraintsBuilder {
    constructor(dependencies) {
        this._dependencies = {...dependencies};
    }

    setAvailableTeams(availableTeams) {
        this._dependencies = {
            ...this._dependencies,
            availableTeams,
        };
    }

    createConstraintsForPlayersInTeam() {
        const {stacking, mappedPlayersByTeams, modes} = this._dependencies;
        return createConstraintsForPlayersInTeam({
            stacking,
            mappedPlayersByTeams,
            modes,
        });
    }

    createConstraintsForForcedCaptains(params) {
        const {stacking, mappedPlayersByTeams, availableTeams} = this._dependencies;
        const {groupNumber, modes} = params;
        return createConstraintsForForcedCaptains({
            stacking,
            teams: availableTeams,
            mappedPlayersByTeams,
            groupNumber,
            modes,
        });
    }

    createConstraintsForSameTeamInStackingGroups() {
        const {stacking, availableTeams} = this._dependencies;
        return createConstraintsForSameTeamInStackingGroups({
            stacking,
            teams: availableTeams,
        });
    }

    createConstraintsForTeamsInStackingGroup() {
        const {stacking, availableTeams} = this._dependencies;
        return createConstraintsForTeamsInStackingGroup({
            stacking,
            teams: availableTeams,
        });
    }

    createObjectiveForRandomlyChosenPlayers(params) {
        const {stacking, availableTeams, mappedPlayersByTeams} = this._dependencies;
        const {projectionPointsPropNamesByModes} = params;
        return createObjectiveForRandomlyChosenPlayers({
            projectionPointsPropNamesByModes,
            teams: availableTeams,
            mappedPlayersByTeams,
            stacking,
        });
    }

    createObjectiveForTeams() {
        const {stacking} = this._dependencies;
        return createObjectiveForTeams({stacking});
    }

    createConstraintsForDesirableTeams(params) {
        const {stacking, lineupsQuantity, availableTeams} = this._dependencies;
        const {usedTeams} = params;
        return createConstraintsForDesirableTeams({
            stacking,
            teams: availableTeams,
            usedTeams,
            lineupsQuantity,
        });
    }

    createConstraintsForTeamsCombinations() {
        const {stacking, lineupsQuantity, lineupTeamsCombinations, rawLineups, availableTeams} = this._dependencies;
        return createConstraintsForTeamsCombinations({
            stacking,
            teams: availableTeams,
            lineupTeamsCombinations,
            lineupsQuantity,
            rawLineups,
        });
    }

    createConstraintsForExcludedTeamsFromStacks(params) {
        return createConstraintsForExcludedTeamsFromStacks(params);
    }
}

export {ConstraintsBuilder};
